'use client';

// RealtimeConversationsProvider — the consumer contract for the admin
// tenant-scoped conversations trigger (BL-1489). Sibling of
// `RealtimeRegistrationsProvider`, built on the same engine.
//
// Owns the ConversationStateStore (subscription, LWW-per-thread fold, cursor,
// cold-seed + reconcile catch-up) and the wake/reconnect triggers; consumers
// read `useOptionalConversationStore()` through the clerk console's re-read
// trigger hook and hold NONE of that plumbing.
//
// Mount it ONCE per page, keyed on the TENANT (a bidder's thread spans
// auctions), and only for a viewer holding `clerking.manage` — the catch-up
// route 403s anyone else (AC 8), and a provider whose every catch-up is refused
// would sit in a reconnect loop for nothing. Two pages mount it: the clerk
// console, and every dashboard page through `StaffMessageCueMount` (BL-2588's
// bidder-message cue). The two never share a page, so no tab holds it twice.

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    type ReactNode,
} from 'react';

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { installReconcileTriggers } from '../../stores/realtime/install-reconcile-triggers';
import {
    ConversationStateStore,
    type ConversationStateCatchupFetcher,
} from '../../stores/realtime/conversation-state-store';
import type { NotifyScheduler } from '../../stores/realtime/entity-state-store';

/**
 * A timer, deliberately — unlike the three sequenced admin streams, which sit
 * at 0 because a dropped frame is DETECTED there (BL-1630). This stream's
 * position is a wall-clock cursor with no gap detection, so the interval is the
 * only recovery for a frame lost on a quiet socket. It matches the inbox's own
 * REST refresh cadence (`INBOX_REFRESH_INTERVAL_MS`), so a missed trigger is at
 * worst one tick late.
 *
 * BL-1340 corrected the constant named above — it read `RAIL_REFRESH_INTERVAL_MS`,
 * which is the BIDDER ROSTER's cadence, not the inbox's. Both are 30_000 today,
 * so nothing misbehaved; the stated coupling simply pointed at the wrong one and
 * would have gone silently false the moment either was retuned alone.
 *
 * This provider is also why ADR 053 — Console Scale §6.2's discharge was
 * corrected in the same item: the console mounts FOUR entity-state providers,
 * not the three that section counted, and this is the fourth.
 */
const RECONCILE_INTERVAL_MS = 30_000;

const ConversationStateStoreContext = createContext<ConversationStateStore | null>(null);

export interface RealtimeConversationsProviderProps {
    /** The tenant whose conversations stream to fold. */
    tenantId: string;
    /** Transport subscriber (shared with the app's RealtimeStack). */
    subscriber: RealtimeSubscriber;
    /** Authenticated catch-up fetcher (mints the topic + cold-seed). */
    catchupFetcher: ConversationStateCatchupFetcher;
    /** Override the reconcile interval (tests pass `0` to disable). */
    reconcileIntervalMs?: number;
    /** Override the coalesced-notify scheduler (BUG-1407 test seam). */
    scheduleNotify?: NotifyScheduler;
    reconcileThrottleMs?: number;
    children: ReactNode;
}

/**
 * Mount around any subtree that re-reads the shared inbox on a conversation
 * change. Owns the store; `useOptionalConversationStore` reads it.
 */
export function RealtimeConversationsProvider({
    tenantId,
    subscriber,
    catchupFetcher,
    reconcileIntervalMs = RECONCILE_INTERVAL_MS,
    reconcileThrottleMs,
    scheduleNotify,
    children,
}: RealtimeConversationsProviderProps) {
    const store = useMemo(
        () => new ConversationStateStore(subscriber, catchupFetcher, tenantId, scheduleNotify),
        [subscriber, catchupFetcher, tenantId, scheduleNotify],
    );

    useEffect(() => {
        void store.start();
        return () => store.stop();
    }, [store]);

    useEffect(() => {
        return installReconcileTriggers({
            reconcileAll: () => store.reconcile(),
            subscriber,
            intervalMs: reconcileIntervalMs,
            reconcileThrottleMs,
        });
    }, [store, subscriber, reconcileIntervalMs, reconcileThrottleMs]);

    return (
        <ConversationStateStoreContext.Provider value={store}>
            {children}
        </ConversationStateStoreContext.Provider>
    );
}

/**
 * The conversations store when a `<RealtimeConversationsProvider>` is above,
 * else `null` — the inbox slide-out mounts inside the rail, which is also
 * rendered for viewers without `clerking.manage` (no provider, no inbox), and
 * in tests that mount the rail bare. Same shape as `useOptionalRegistrationStore`.
 *
 * @returns the tenant's conversation store, or `null` when no provider is mounted.
 */
export function useOptionalConversationStore(): ConversationStateStore | null {
    return useContext(ConversationStateStoreContext);
}
